import React, { useState } from 'react';
import { Search, Filter, RefreshCw, AlertCircle, Shield, UserX, UserCheck } from 'lucide-react';

import {
  useSuperAdminUsers,
  useUpdateUserStatus,
} from '@/features/super-admin/hooks/useSuperAdminUsers';
import UserStatusModal from '@/features/super-admin/components/users/UserStatusModal';
import { formatJalaliDate } from '@/lib/format';
import { formatNumber, persianDigits } from '@/features/reports/lib/format';
import { getPersianLabel, persianDisplayText } from '@/lib/persianUi';

const USER_ROLES = [
  { value: 'ALL', label: 'همه نقش‌ها' },
  { value: 'customer', label: 'خریدار' },
  { value: 'vendor', label: 'فروشنده' },
  { value: 'irancharsouq_admin', label: 'ادمین ایران چار سوق' },
  { value: 'super_admin', label: 'سوپر ادمین' },
];

export default function UserManagementTable() {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [page, setPage] = useState(1);

  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading, isFetching, error, refetch } = useSuperAdminUsers({
    page,
    size: 10,
    search: appliedSearch || undefined,
    role: roleFilter !== 'ALL' ? roleFilter : undefined,
  });

  const users = data?.items ?? [];
  const totalPages = data?.pages ?? 0;

  const updateUserStatus = useUpdateUserStatus();

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = search.trim();

    if (trimmed === appliedSearch && page === 1) {
      refetch();
    } else {
      setAppliedSearch(trimmed);
      setPage(1);
    }
  };

  const openStatusModal = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleStatusConfirm = (updatedStatus) => {
    if (!selectedUser) return;

    updateUserStatus.mutate(
      { userId: selectedUser.id, isActive: updatedStatus },
      {
        onSuccess: () => setIsModalOpen(false),
        onError: (err) => {
          console.error(err);
          alert('خطا در تغییر وضعیت کاربر');
        },
      }
    );
  };

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">مدیریت کاربران</h1>
          <p className="admin-page-subtitle">
            مشاهده، فیلتر و مدیریت وضعیت تمامی کاربران سیستم
          </p>
        </div>

        <button onClick={() => refetch()} className="admin-refresh-btn" disabled={isFetching}>
          <RefreshCw size={16} className={isFetching ? 'spinning' : ''} />
          <span>بروزرسانی</span>
        </button>
      </div>

      <div className="card admin-toolbar">
        <form onSubmit={handleSearchSubmit} className="admin-search">
          <input
            type="text"
            placeholder="جستجو با نام، ایمیل یا شماره تماس..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Search size={16} />
        </form>

        <div className="admin-filter">
          <Filter size={16} />
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
          >
            {USER_ROLES.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card admin-table-card">
        {error && (
          <div className="admin-table-error">
            <AlertCircle size={16} />
            <span>خطا در دریافت لیست کاربران</span>
          </div>
        )}

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>کاربر</th>
                <th>شماره تماس</th>
                <th>نقش</th>
                <th>تاریخ ثبت‌نام</th>
                <th>وضعیت</th>
                <th style={{ textAlign: 'center' }}>عملیات</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="admin-table-empty">
                    در حال دریافت اطلاعات...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="admin-table-empty">
                    کاربری یافت نشد.
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const roleTypes = user.roles?.map((item) => item.type).filter(Boolean) ?? [];
                  const roleLabels = roleTypes.map(
                    (type) => getPersianLabel(
                      Object.fromEntries(USER_ROLES.filter((item) => item.value !== 'ALL').map((item) => [item.value, item.label])),
                      type,
                      'superAdmin.userRole',
                    )
                  );

                  return (
                    <tr key={user.id}>
                      <td>
                        <p className="admin-cell-primary">
                          {user.first_name
                            ? `${persianDisplayText(user.first_name, 'بدون نام')} ${persianDisplayText(user.last_name, '')}`
                            : 'بدون نام'}
                        </p>
                        <p className="admin-cell-secondary">{user.email || '—'}</p>
                      </td>

                      <td className="admin-cell-secondary" style={{ fontFamily: 'monospace' }}>
                        {persianDigits(user.mobile_phone) || '—'}
                      </td>

                      <td>
                        <span className="role-badge">
                          <Shield size={12} />
                          {roleLabels.length > 0 ? roleLabels.join('، ') : 'بدون نقش'}
                        </span>
                      </td>

                      <td className="admin-cell-secondary">
                        {user.created_at ? formatJalaliDate(user.created_at) : '—'}
                      </td>

                      <td>
                        <span
                          className={`status-badge-sm ${
                            user.is_active ? 'status-active-badge' : 'status-inactive-badge'
                          }`}
                        >
                          {user.is_active ? 'فعال' : 'غیرفعال / مسدود'}
                        </span>
                      </td>

                      <td style={{ textAlign: 'center' }}>
                        <button
                          onClick={() => openStatusModal(user)}
                          className={`btn-sm ${user.is_active ? 'btn-sm-danger' : 'btn-sm-success'}`}
                        >
                          {user.is_active ? (
                            <>
                              <UserX size={14} />
                              <span>غیرفعال‌سازی</span>
                            </>
                          ) : (
                            <>
                              <UserCheck size={14} />
                              <span>فعال‌سازی</span>
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="admin-pagination">
            <span>
              صفحه {formatNumber(page)} از {formatNumber(totalPages)}
            </span>

            <div className="admin-pagination-btns">
              <button disabled={page === 1} onClick={() => setPage((p) => Math.max(p - 1, 1))}>
                قبلی
              </button>
              <button disabled={page === totalPages} onClick={() => setPage((p) => Math.min(p + 1, totalPages))}>
                بعدی
              </button>
            </div>
          </div>
        )}
      </div>

      <UserStatusModal
        isOpen={isModalOpen}
        user={selectedUser}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleStatusConfirm}
      />
    </div>
  );
}